<script lang="ts">
  import { Control, setupControl } from "./controls";
  import FormField from "./FormField.svelte";
  import { Validator, type ValidatorMessages, type ValidatorResult } from "./validators";
  import type { ControlValidator } from "./validators";

  export let type: "email" | "text" | "password" | "number" | "date" | "hidden" = "text";
  export let rows: number = 1;
  export let placeholder: string = "";
  export let autocomplete: string = undefined;
  export let attrs: { [key: string]: any } = undefined;

  // either set name and validators
  export let name: string = null;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: any = undefined;
  // ... or bind a control
  export let control: Control = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;

  let input: HTMLInputElement | HTMLTextAreaElement;
  function inputValidator(control: Control): ValidatorResult {
    // FireFox fix:
    if (input && input.type === "email" && input.value !== input.value?.trim()) {
      control.value = input.value = input.value.trim();
    }
    if (input && "checkValidity" in input && !input.checkValidity()) {
      return {
        validator: "inputValidator",
        message: input.validationMessage,
      };
    }
  }

  const changes = setupControl(Control, {
    name,
    validators: [...validators, ...(required ? [Validator.required] : []), inputValidator],
    value,
    control,
    standalone,
  });
  $: ctrl = $changes;

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = value;
  }

  let empty = false;
  function detectEmpty(input: HTMLInputElement | HTMLTextAreaElement) {
    var change = () => (empty = input.value === "");

    const events = ["change", "keyup", "input", "propertychange"];
    events.forEach((event) => input.addEventListener(event, change, true));

    change();

    return {
      destroy: () => events.forEach((event) => input.removeEventListener(event, change, true)),
    };
  }
</script>

<FormField bind:control={ctrl} displayName={displayName || placeholder} {errors} {standalone} on:change>
  {#if type === "hidden"}
    <input class="input-control" bind:this={input} class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="hidden" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
  {:else}
    <!-- svelte-ignore a11y-label-has-associated-control -->
    <label class="input-control-label">
      {#if type === "password"}
        <input class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="password" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
      {:else if type === "email"}
        <input class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="email" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
      {:else if type === "number"}
        <input class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="number" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
      {:else if type === "date"}
        <input class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="date" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
      {:else if rows > 1}
        <textarea class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {rows} {...attrs} />
      {:else}
        <input class="input-control" bind:this={input} use:detectEmpty class:empty class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} type="text" {name} {autocomplete} on:focus on:blur bind:value={ctrl.value} on:blur={(_) => ctrl.touch()} {...attrs} />
      {/if}

      <div class="control-label">
        <slot>
          {placeholder}{#if required}&nbsp;*{/if}
        </slot>
      </div>
    </label>
  {/if}
</FormField>

<style lang="scss" global>
  .input-control {
    margin: 0;
    font-family: inherit;
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    background-color: #fff;
    background-clip: padding-box;
    border: 2px solid #d9d9d9;
    appearance: none;
    border-radius: 28px;
    min-height: 56px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    box-sizing: border-box;

    &:focus {
      // border-color: #86b7fe;
      // outline: 0;
      // box-shadow: 0 0 0 0.25rem rgb(13 110 253 / 25%);
      border-color: #000;
    }

    &.input-error {
      border-color: #f5c6cb;
      background-color: #f8d7da;
    }
  }

  .input-control-sm {
    padding: 0.175rem 0.75rem;
    min-height: auto;
  }

  .textarea.input-control {
    border-radius: 28px;
    resize: none;
  }

  $fg-color: #000;

  .input-control-label {
    position: relative;
    width: 100%;
    margin: 0;

    .control-label {
      position: absolute;
      top: 0.5em;
      left: 20px;
      font-size: 18px;
      color: rgba($fg-color, 0.4);
      line-height: 1;
      padding-top: 9px;
      padding-left: 7px;
      transition: all ease 300ms;
      user-select: none;
      pointer-events: none;
    }

    .input-control:focus-within ~ .control-label,
    .input-control:not(.empty) ~ .control-label {
      top: -23px;
      font-size: 14px;
      padding-top: 8px;
      padding-left: 2px;
      color: rgba($fg-color, 0.6);
    }
  }
</style>
